import { authService } from '@/features/auth/services/authService';
import { getNodeId } from '@/features/projects/MultiProjectContext';
import type { ProjectNode } from '@/shared/types/projectData';
import { transformNodeFromBackend } from '@/shared/utils/fieldNameTransform';

const hasOwn = (value: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const normalizeStringOrNull = (value: unknown): string | null => {
  if (value === null) return null;
  return typeof value === 'string' ? value : null;
};

const normalizeCustomFields = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const nextFields = { ...(value as Record<string, unknown>) };
  if ('blockDocument' in nextFields || 'block_document' in nextFields) {
    const blockDocument = 'block_document' in nextFields
      ? nextFields.block_document
      : nextFields.blockDocument;
    delete nextFields.blockDocument;
    delete nextFields.block_document;
    if (blockDocument != null) {
      nextFields.block_document = blockDocument;
    }
  }
  return nextFields;
};

export const buildNoteDocumentUpdatePayload = (
  changes: Partial<ProjectNode>,
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};

  if (hasOwn(changes, 'title')) {
    payload.title = changes.title;
  }

  if (hasOwn(changes, 'content') || hasOwn(changes, 'description')) {
    const content = changes.content ?? changes.description ?? '';
    payload.content = content;
    payload.description = changes.description ?? content;
  }

  if (hasOwn(changes, 'startDate')) {
    payload.start_date = normalizeStringOrNull(changes.startDate);
  }

  if (hasOwn(changes, 'endDate')) {
    payload.end_date = normalizeStringOrNull(changes.endDate);
  }

  if (hasOwn(changes, 'category')) {
    payload.category = changes.category ?? null;
  }

  if (hasOwn(changes, 'workstream')) {
    payload.workstream = changes.workstream ?? null;
  }

  if (hasOwn(changes, 'developmentRoles')) {
    payload.development_roles = changes.developmentRoles ?? [];
  }

  if (hasOwn(changes, 'visibilityTier')) {
    payload.visibility_tier = changes.visibilityTier ?? null;
  }

  if (hasOwn(changes, 'visibleToAccountIds')) {
    payload.visible_to_account_ids = changes.visibleToAccountIds ?? [];
  }

  if (hasOwn(changes, 'classificationSource')) {
    payload.classification_source = changes.classificationSource ?? null;
  }

  if (hasOwn(changes, 'classificationConfidence')) {
    payload.classification_confidence = changes.classificationConfidence ?? null;
  }

  if (hasOwn(changes, 'tags')) {
    payload.tags = changes.tags ?? [];
  }

  if (hasOwn(changes, 'mentions')) {
    payload.mentions = changes.mentions ?? [];
  }

  if (hasOwn(changes, 'customFields')) {
    payload.custom_fields = normalizeCustomFields(changes.customFields);
  }

  if (hasOwn(changes, 'attachments')) {
    payload.attachments = changes.attachments ?? [];
  }

  if (hasOwn(changes, 'attachmentCount')) {
    payload.attachment_count = changes.attachmentCount ?? 0;
  }

  if (hasOwn(changes, 'hasImages')) {
    payload.has_images = Boolean(changes.hasImages);
  }

  if (hasOwn(changes, 'hasDocuments')) {
    payload.has_documents = Boolean(changes.hasDocuments);
  }

  return payload;
};

export const persistNoteDocumentUpdate = async (
  projectId: string,
  nodeId: string,
  changes: Partial<ProjectNode>,
): Promise<ProjectNode | null> => {
  const payload = buildNoteDocumentUpdatePayload(changes);
  if (Object.keys(payload).length === 0) {
    return null;
  }

  const response = await authService.authenticatedFetch(
    `/api/v1/projects/${projectId}/documents/${getNodeId(nodeId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `Failed to persist note document update (${response.status})`);
  }
  const result = await response.json();
  const document = transformNodeFromBackend(result?.data?.document);
  if (!document || typeof document !== 'object' || typeof document.id !== 'string'
    || getNodeId(document.id) !== getNodeId(nodeId) || document.type !== 'document'
    || (document.projectId && document.projectId !== projectId)) {
    throw new Error('The note save response did not contain the saved document');
  }
  return document as ProjectNode;
};
